<template>
  <div class="input multidropdown">
    <div class="fields">
      <span class="inputlabel" v-if="label">{{ label }}</span>
      <div class="custom-dropdown" ref="outsideTarget">
        <button class="multidropdown-button" role="listbox" aria-expanded="false" :aria-controls="name"
          @click="show = !show" :aria-label="'Välj ' + label + '. Redan valda: ' + selectedDisplay">
          {{ selectedDisplay }}
        </button>
        <!-- TODO: Popover istället så den håller sig på skärmen -->
        <div class="multidropdown-filter collapse" :class="{ in: show }" :id="name" ref="dropdownContainer"
          :style="{ marginLeft: positionAdjustment ? positionAdjustment + 'px' : undefined }">
          <span class="titel">Välj {{ label }}</span>
          <ul class="list alla-val">
            <slot name="firstItem" />
          </ul>
          <ul class="list">
            <li role="option" class="list-option" v-for="option in options" :key="option.value">
              <input type="checkbox" :id="name + '_' + option.value" :value="option.value" :name="name"
                :data-longname="option.label" :checked="model.includes(option.value)" @change="(e) => toggle(e)" />
              <label tabindex="-1" :for="name + '_' + option.value" class="container-choice">
                <Svgicon iconName="check" aria-hidden="true" focusable="false" />
                <span class="txt" v-html="option.label"></span>
              </label>
            </li>
          </ul>
          <div v-if="delayUpdate">
            <button @click="
              () => {
                doEmit();
                show = false;
              }
            " class="btn btn-submit btn-primary">
              Filtrera
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';

import type { Option } from '../../types/GenericTypes';
import Svgicon from './svgicon.vue';

const model = defineModel<Array<string>>({ default: [] });
const emit = defineEmits([
  'update:modelValue', //triggas när ändringen genomförs. endera direkt eller via filter om delayUpdate är true
  'onToggle', //Triggas på alla i/urkryssningar, även om delay är satt
]);
const show = ref(false);
const outsideTarget = useTemplateRef<HTMLElement>('outsideTarget');
const dropdownContainer = ref<HTMLElement>();
const positionAdjustment = ref<number | undefined>(undefined);

const props = defineProps({
  name: {
    type: String,
    required: false,
  },
  label: {
    type: String,
    required: true,
  },
  options: {
    type: Array<Option>,
    required: true,
  },
  delayUpdate: {
    //visar extra filterknapp och triggar inte ändringarna förrän den trycks
    type: Boolean,
    default: false,
  },
  formatSelected: {
    type: Function,
    default: (selectedValues: string[], _selectedLabels: string[]) => selectedValues.join(', '),
  },
  autoSize: {
    type: Boolean,
    default: false,
  },
});

onClickOutside(outsideTarget, () => (show.value = false));

const doEmit = () => {
  emit('update:modelValue', model.value);
};
const toggle = (e: Event) => {
  const value = (e.target as HTMLInputElement).value;
  const index = model.value.indexOf(value);
  if (index === -1) {
    model.value.push(value);
  } else {
    model.value.splice(index, 1);
  }
  emit('onToggle', model.value);
  if (!props.delayUpdate) {
    doEmit();
  }
};
const reposition = () => {
  const el = dropdownContainer.value;
  if (el) {
    const rect = el.getBoundingClientRect();
    const w = document.documentElement.clientWidth;

    const outside = w - rect.x - rect.width + (positionAdjustment?.value ?? 0);
    if (outside < 0) {
      console.log('multidropdown:repositioning', outside);

      positionAdjustment.value = outside;
    }
  }
};

watch(
  show,
  (newValue) => {
    if (props.autoSize && newValue) {
      reposition();
    }
  },
  { flush: 'post' }
);

const selectedDisplay = computed(() => {
  var sel = props.options?.filter((o) => model.value.includes(o.value));
  return props.formatSelected(
    sel.map((o) => o.value),
    sel.map((o) => o.label)
  );
});
</script>
